import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  useGetCurrentUserId,
  useIsAuthenticated,
  useIsAuthenticating,
} from '@wwe-portal/web/providers/stores/auth-store';
import { useDripFeatureConfigs } from '@wwe-portal/web/providers/queries/user-info';
import {
  getCurrencies,
  getOfferById,
  getOfferStocks,
  getConsolidatedOffers,
  getSegmentedOffers,
  getSegmentedOffersForNonLogin,
  getActiveDripOffers,
  dismissOtoOffer,
} from '@wwe-portal/web/services/client/offer';
import { addDDError } from '@wwe-portal/web/libs/analytics/datadog';
import {
  QUERY_KEY_CURRENCIES,
  QUERY_KEY_OFFER_V2,
  QUERY_KEY_USER_STOCKS_V2,
} from '@wwe-portal/web/utils/constants/keys';
import {
  CategoryType,
  OfferData,
  StockResponse,
  ConsolidatedOffersResponse,
} from '@wwe-portal/entities/offer.type';
import {
  getOfferDate,
  isOfferPurchasable,
  hasDripConflict,
  isDripOffer,
} from '@wwe-portal/web/utils/offer';
import { buildOfferMaps } from '@wwe-portal/web/utils/offer/build-offer-maps';
import { AxiosResponse } from 'axios';
import {
  MAX_REFETCH_INTERVAL,
  MIN_REFETCH_INTERVAL,
} from '@wwe-portal/web/utils/config';
import { getOfferByIdKey, getConsolidatedOffersKey } from './offer.key';
import { useEnabledFlag } from '@wwe-portal/web/components/common/feature-flag';

const QUERY_KEY_PREFIX = 'segmentedOffers';

export const useGetSegmentedOffers = () => {
  const isAuthenticated = useIsAuthenticated();
  const { enabled, isLoading } = useEnabledFlag('segmentation.offers');

  return useQuery({
    queryKey: [QUERY_KEY_PREFIX, { enabled, isLoading, isAuthenticated }],

    queryFn: async () => {
      const defaultValue: string[] = [];

      if (!enabled) return defaultValue;

      const getOffersFunction = isAuthenticated
        ? getSegmentedOffers
        : getSegmentedOffersForNonLogin;
      const response = await getOffersFunction();

      const segmentedOffers = response.data.data.offers;
      if (segmentedOffers == null) {
        return defaultValue;
      }

      return segmentedOffers;
    },

    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: false,
  });
};

export const useInvalidateSegmentedOffers = () => {
  const queryClient = useQueryClient();
  return () => {
    return queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_PREFIX],
    });
  };
};

export const useSegmentedOfferFilter = () => {
  const enabled = useEnabledFlag('segmentation.offers');
  const { isLoading, data } = useGetSegmentedOffers();
  const segmentedOffers = useMemo(() => new Set(data ?? []), [data]);

  const isSegmentedOffer = useCallback(
    <T extends { OffersID: string }>({ OffersID }: T) =>
      isLoading || !OffersID || !enabled || segmentedOffers.has(OffersID),
    [enabled, isLoading, segmentedOffers]
  );

  const filterSegmentedOffers = useCallback(
    <T extends { OffersID: string }>(offers: T[] | undefined) =>
      offers?.filter(isSegmentedOffer) ?? [],
    [isSegmentedOffer]
  );

  return {
    isLoading,
    isSegmentedOffer,
    filterSegmentedOffers,
  };
};

const useGetUserState = () => {
  const isAuthenticated = useIsAuthenticated();
  const userId = useGetCurrentUserId();
  const currentUserLoading = useIsAuthenticating();
  return {
    isAuthenticated,
    userId,
    currentUserLoading,
  };
};

const useIsOfferPurchasable = () => {
  const isAuthenticated = useIsAuthenticated();
  const isAuthenticating = useIsAuthenticating();
  const { stockMap, isLoading: isLoadingStock } = useGetUserStocks();
  const { isSegmentedOffer, isLoading: isLoadingSegmentedOffer } =
    useSegmentedOfferFilter();
  const checkIfOfferPurchasable = useCallback(
    (offer: OfferData) => {
      return isOfferPurchasable(offer, {
        isAuthenticated,
        stockMap,
        isSegmentedOffer,
      });
    },
    [isAuthenticated, stockMap, isSegmentedOffer]
  );

  const isLoading =
    isAuthenticating || isLoadingStock || isLoadingSegmentedOffer;

  return {
    checkIfOfferPurchasable,
    isLoading,
  };
};

const useIsOfferPurchasableWithoutSegmentation = (
  hasDripOffersInCatalog: boolean
) => {
  const isAuthenticated = useIsAuthenticated();
  const isAuthenticating = useIsAuthenticating();
  const { stockMap, isLoading: isLoadingStock } = useGetUserStocks();
  const { data: activeDripOffers, isLoading: isLoadingDrip } =
    useGetActiveDripOffers(hasDripOffersInCatalog);
  const { pinnedDripByDays, isLoading: isLoadingFeatureConfigs } =
    useDripFeatureConfigs(hasDripOffersInCatalog);

  const activeDripDurations = useMemo(() => {
    const durations = new Set<number>();
    if (activeDripOffers) {
      for (const drip of activeDripOffers) {
        if (!drip.is_complete) {
          durations.add(drip.total_days);
        }
      }
    }
    return durations;
  }, [activeDripOffers]);

  const checkIfOfferPurchasable = useCallback(
    (offer: OfferData) => {
      // First check: active drip conflict (existing logic)
      if (hasDripConflict(offer, activeDripDurations)) return false;

      // Second check: pinned drip filtering (new A/B testing logic)
      if (isDripOffer(offer)) {
        const duration = offer.dc?.b0 ?? 7; // DEFAULT_DRIP_DAYS fallback
        if (pinnedDripByDays.has(duration)) {
          const pinnedOfferId = pinnedDripByDays.get(duration);
          // Only allow the pinned offer for this duration
          if (offer.OffersID !== pinnedOfferId) return false;
        }
      }

      // Default purchasability check
      return isOfferPurchasable(offer, {
        isAuthenticated,
        stockMap,
        isSegmentedOffer: undefined,
      });
    },
    [isAuthenticated, stockMap, activeDripDurations, pinnedDripByDays]
  );

  const isLoading =
    isAuthenticating ||
    isLoadingStock ||
    isLoadingDrip ||
    isLoadingFeatureConfigs;

  return {
    checkIfOfferPurchasable,
    isLoading,
  };
};

export const useGetOfferById = (offerId: string) => {
  const {
    data: rawResponse,
    isLoading: isLoadingOffer,
    ...rest
  } = useQuery({
    queryKey: getOfferByIdKey(offerId),
    queryFn: () => getOfferById(offerId),
  });
  const { checkIfOfferPurchasable, isLoading: isLoadingChecker } =
    useIsOfferPurchasable();

  const isLoading = isLoadingChecker || isLoadingOffer;

  const rawData = useMemo(
    () => rawResponse?.data.data.offerData,
    [rawResponse]
  );
  const isPurchasable = useMemo(() => {
    if (!rawData) return false;
    return checkIfOfferPurchasable(rawData);
  }, [rawData, checkIfOfferPurchasable]);

  const data = useMemo(() => {
    if (!rawData) return undefined;
    if (isPurchasable) {
      return rawData;
    }
    return undefined;
  }, [rawData, isPurchasable]);

  return {
    data,
    isPurchasable,
    rawData,
    isLoading,
    ...rest,
  };
};

export const useInvalidateMobileOffers = () => {
  const { isAuthenticated, userId } = useGetUserState();
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_OFFER_V2, 'mobile', { userId, isAuthenticated }],
    });
  };
};

const buildStockMap = (data: AxiosResponse<StockResponse> | undefined) => {
  if (!data) return undefined;
  const map = new Map<string, number>();
  for (const stockItem of data.data.data) {
    map.set(stockItem.OffersID, stockItem.StockQuantity);
  }
  return map;
};

export const useGetUserStocks = () => {
  const isAuthenticated = useIsAuthenticated();
  const userId = useGetCurrentUserId();
  const { data: stockMap, ...queryResult } = useQuery({
    queryKey: [QUERY_KEY_USER_STOCKS_V2, { userId, isAuthenticated }],
    queryFn: async () => {
      const result = await getOfferStocks();
      return buildStockMap(result);
    },
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return {
    stockMap,
    ...queryResult,
  };
};

export const useGetActiveDripOffers = (hasDripOffersInCatalog = false) => {
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: ['active-drip-offers', isAuthenticated],
    queryFn: async () => {
      const response = await getActiveDripOffers();
      return response.data.data.data ?? [];
    },
    enabled: isAuthenticated && hasDripOffersInCatalog,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useGetOffersStock = () => {
  const isAuthenticated = useIsAuthenticated();
  const userId = useGetCurrentUserId();
  return useQuery({
    queryKey: [QUERY_KEY_USER_STOCKS_V2, { userId, isAuthenticated }],
    queryFn: () => getOfferStocks(),
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useInvalidateUserStocks = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useIsAuthenticated();
  const userId = useGetCurrentUserId();

  return async () => {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_USER_STOCKS_V2, { userId, isAuthenticated }],
    });
  };
};

export const useStandardOfferMap = () => {
  const { data: consolidatedData, isLoading } = useGetConsolidatedOffers();
  const { offerMap, categoryMap } = useMemo(
    () => buildOfferMaps(consolidatedData),
    [consolidatedData]
  );

  return { offerMap, isLoading, categoryMap };
};

const calculateRefetchInterval = (queryResult: {
  state: { data: ConsolidatedOffersResponse['data'] | undefined };
}) => {
  const data = queryResult.state.data;
  if (!data) return false;

  const now = Date.now();
  let nearestFutureDate = Infinity;

  const allCategories = [
    data.featured,
    data.special,
    data.daily,
    data.rubies,
    data.cashPacks,
    data.webCoins,
    data.rewards,
    data.webCoinDaily,
    data.tournament,
    data.clubCoins,
  ];

  for (const sections of allCategories) {
    if (!sections) continue;
    for (const section of sections) {
      for (const offer of section.offers) {
        const endTime = getOfferDate(offer.EndDate)?.getTime();
        if (endTime && endTime > now && endTime < nearestFutureDate) {
          nearestFutureDate = endTime;
        }
        const startTime = getOfferDate(offer.StartDate)?.getTime();
        if (startTime && startTime > now && startTime < nearestFutureDate) {
          nearestFutureDate = startTime;
        }
      }
    }
  }

  if (nearestFutureDate === Infinity) return false;
  const diff = nearestFutureDate - now;
  return diff > 0
    ? Math.min(Math.max(diff, MIN_REFETCH_INTERVAL), MAX_REFETCH_INTERVAL)
    : false;
};

export const useGetCurrencies = () => {
  return useQuery({
    queryKey: [QUERY_KEY_CURRENCIES],
    queryFn: () => getCurrencies(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useGetConsolidatedOffers = (categories?: CategoryType[]) => {
  const isAuthenticated = useIsAuthenticated();
  const userId = useGetCurrentUserId();
  const {
    data: rawData,
    isLoading: isLoadingOffer,
    ...rest
  } = useQuery({
    queryKey: getConsolidatedOffersKey({ categories, userId, isAuthenticated }),
    queryFn: async () => {
      const response = await getConsolidatedOffers(categories);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: calculateRefetchInterval,
  });

  const hasDripOffersInCatalog = useMemo(() => {
    if (!rawData) return false;
    return Object.values(rawData).some((sections) =>
      sections?.some((section) => section.offers.some(isDripOffer))
    );
  }, [rawData]);

  const { checkIfOfferPurchasable, isLoading: isLoadingChecker } =
    useIsOfferPurchasableWithoutSegmentation(hasDripOffersInCatalog);

  const isLoading = isLoadingChecker || isLoadingOffer;
  const data = useMemo(() => {
    if (!rawData) return rawData;

    const filteredData: ConsolidatedOffersResponse['data'] = {};

    Object.keys(rawData).forEach((categoryKey) => {
      const sections = rawData[categoryKey as keyof typeof rawData];
      if (sections) {
        filteredData[categoryKey as keyof typeof rawData] = sections.map(
          (sectionData) => ({
            ...sectionData,
            offers: sectionData.offers.filter(checkIfOfferPurchasable),
          })
        );
      }
    });

    return filteredData;
  }, [rawData, checkIfOfferPurchasable]);

  return {
    data,
    rawData,
    isLoading,
    ...rest,
  };
};

const removeOfferFromConsolidatedData = (
  cached: ConsolidatedOffersResponse['data'] | undefined,
  offerId: string
) => {
  if (!cached) return cached;

  const filtered: ConsolidatedOffersResponse['data'] = {};
  Object.keys(cached).forEach((categoryKey) => {
    const sections = cached[categoryKey as keyof typeof cached];
    if (sections) {
      filtered[categoryKey as keyof typeof cached] = sections.map(
        (sectionData) => ({
          ...sectionData,
          offers: sectionData.offers.filter(
            (offer) => offer.OffersID !== offerId
          ),
        })
      );
    }
  });
  return filtered;
};

export const useDismissOtoOffer = () => {
  const queryClient = useQueryClient();

  const removeOfferFromCaches = (offerId: string) => {
    queryClient.setQueriesData<ConsolidatedOffersResponse['data']>(
      { queryKey: ['consolidated-offers'] },
      (cached) => removeOfferFromConsolidatedData(cached, offerId)
    );
    queryClient.removeQueries({ queryKey: getOfferByIdKey(offerId) });
  };

  return useMutation({
    mutationFn: (offerId: string) => dismissOtoOffer(offerId),
    onError: (error, offerId) => {
      addDDError(error, { feature: 'dismiss-oto-offer', offerId });
    },
    // Hide the offer locally even if the dismiss request failed, so the
    // user never keeps interacting with an offer the game server may
    // already consider dismissed.
    onSettled: (_data, _error, offerId) => {
      removeOfferFromCaches(offerId);
    },
  });
};

export const useInvalidateConsolidatedOffers = () => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: ['consolidated-offers'],
    });
  };
};
